import React, { useState, useRef, useEffect } from 'react';
import { View, Animated, Easing } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import { fullWidth, scale as initialScale, width } from '@components/frame';
import { font as initialFont } from '@styles/fonts';

import AnimatedHeader from '@components/common/AnimatedHeader';
import {
  setSecureValue,
  getSecureValue,
  LEVELS,
  isWeb,
  NC_FILE_LEVELS,
  PART_LEVELS,
  TOOL_LEVELS,
  IMAGE_DOCUMENTS_LEVELS,
  controlAPICall,
  isPhone,
  addQueryParams,
  removeQueryParam,
  searchDisplayMode,
  capitalizeFirstLetter,
  LEVELS_WITH_FLAGS,
  filterByKeys,
} from '../../../helpers';
import { useNavigation, useNavigationState } from '@react-navigation/native';

import ColorWrapper from '@styles/colors';

import Header from './header';
import SearchResults from './searchResults';
import History from './history';
import Level from './level';

import Parts from '@components/search/parts';
import Operations from '@components/search/operations';
import ProductionBatches from '@components/search/productionBatches';
import ProductionOrders from '@components/search/productionOrders';
import NCFiles from '@components/search/ncFiles';
import Tools from '@components/search/tools';
import ImagesDocuments from '@components/search/images';
import NoResult from './noResult';

import { generateImageURL } from '@reducers/blobStorage';
import { setSelectedNCFile } from '@reducers/manufacture';
import { updateImageMetadata } from '@reducers/imageHandling';
import {
  globalSuggestion,
  partsSuggestion,
  operationsSuggestion,
  productionBatchesSuggestion,
  productionOrdersSuggestion,
  toolsSuggestion,
  ncFilesSuggestion,
  documentsSuggestion,
  globalSearch,
  partsSearch,
  operationsSearch,
  productionBatchesSearch,
  productionOrdersSearch,
  ncFilesSearch,
  toolsSearch,
  documentsSearch,
  setShouldRefetchData,
} from '../../../reducers/search';

import { setGlobalSelectedLevel } from '@reducers/globalAdd';
import Suggestion from './suggestion';
import Statistic from './statistic';

import MobileHeader from './v2/MobileHeader';

import { NC_FILE_SELECT, PART_SELECT, PRODUCTION_ORDERS_SELECT, OPERATION_SELECT, IMAGES_DOCUMENTS_SELECT } from './select';
import { BATCH_LIST_SELECT, SMART_FACTORY, TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { setBatchList, setBatchListContainerHeight, setBatchListYPosition } from '@reducers/batch';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useGridApiRef } from '@v2/pages/common/hooks/useGridApiRef';
import { getMachines } from '@reducers/machines';
import { useListenForClosedOperations } from '@hooks/useListenForClosedOperations';
import { useListenForUploadedOperationImages } from '@hooks/useListenForUploadedOperationImages';
import { searchUsers } from '@reducers/membership';
import SearchMachines from '@components/search/machines';
import SearchPersons from '@components/search/persons';
import { getPersistedTableConfig } from '@components/common/DataGrid';
import { clearPersistedFilters, clearPersistedSort } from '@components/common/DataGrid/helpers';
import { trackEvent, trackOpenedEvent, TRACKING_EVENTS, levelToSearchItemType } from '@services/tracking';

let searchTimeout = null;

const RenderSearchComponent = React.memo((props) => {
  if (!props.searchResult.length && props.isSearchTriggered && !props.isTableMode) {
    return <NoResult {...{ C: props.C, value: props.isSearchTriggered }} />;
  }

  // Safely get navigation context
  let navigation = null;
  let isInNavigationContext = false;

  try {
    const navigationState = useNavigationState((state) => !!state);
    isInNavigationContext = navigationState !== undefined;
    navigation = useNavigation();
  } catch (error) {
    // Not in navigation context - this is expected in popup mode
    isInNavigationContext = false;
    navigation = null;
  }

  const getMarginTop = (props) => {
    if (props.isBlockMode) {
      return isPhone && !props?.IS_NARROW_WEB ? -22 : !isWeb ? -37 : props?.IS_NARROW_WEB ? 10 : -5;
    }

    if (props.isSmallMode) {
      return isPhone && !props?.IS_NARROW_WEB ? -5 : !isWeb ? -20 : props?.IS_NARROW_WEB ? 10 : -5;
    }

    if (props.isLineMode) {
      return isPhone && !props?.IS_NARROW_WEB ? -9 : !isWeb ? -25 : props?.IS_NARROW_WEB ? 10 : 0;
    }

    if (props.isInfoTilesMode) {
      return isPhone && !props?.IS_NARROW_WEB ? -18 : !isWeb ? -15 : props?.IS_NARROW_WEB ? 12 : -4;
    }

    switch (props.displayMode) {
      case searchDisplayMode.tableCompact:
        return !props?.IS_NARROW_WEB ? -20 : 10;
      case searchDisplayMode.tableStandard:
        return !props?.IS_NARROW_WEB ? -36 : -6;
      default:
        return 0;
    }
  };

  useEffect(() => {
    if (isWeb && navigation) {
      navigation.setOptions({ title: `${LEVELS_WITH_FLAGS[props.level].name} - ${SMART_FACTORY}` });
    }
  }, [props.level, navigation]);

  switch (props.level) {
    case LEVELS.Parts:
      return <Parts {...props} />;
    case LEVELS.Operations:
      return <Operations {...props} />;
    case LEVELS.ProductionBatches:
      return (
        <ProductionBatches
          {...props}
          searchResult={
            props.isInfoTilesMode && !!props.searchResult?.length
              ? props.searchResult.slice(0, props.batchListOffsetForTileMode + props.topAmount)
              : props.searchResult
          }
          marginTop={getMarginTop(props)}
        />
      );
    case LEVELS.ProductionOrders:
      return <ProductionOrders {...props} />;
    case LEVELS.NCFiles:
      return <NCFiles {...props} />;
    case LEVELS.Tools:
      return <Tools {...props} />;
    case LEVELS.ImagesDocuments:
      return <ImagesDocuments {...props} isSearchScreen />;
    case LEVELS.Machines:
      return <SearchMachines {...props} />;
    case LEVELS.Persons:
      return <SearchPersons {...props} />;
    default:
      return null;
  }
});

const Search = ({
  C,
  scale = initialScale,
  font = initialFont,
  globalSuggestion,
  partsSuggestion,
  operationsSuggestion,
  productionBatchesSuggestion,
  productionOrdersSuggestion,
  toolsSuggestion,
  ncFilesSuggestion,
  documentsSuggestion,
  globalSearch,
  generateImageURL,
  partsSearch,
  operationsSearch,
  productionBatchesSearch,
  productionOrdersSearch,
  ncFilesSearch,
  toolsSearch,
  documentsSearch,
  getMachines,
  searchUsers,
  setSelectedNCFile,
  featureFlags,
  setGlobalSelectedLevel,
  route,
  isWithERP,
  setBatchList,
  setBatchListYPosition,
  setBatchListContainerHeight,
  batchList,
  shouldRefetchData,
  setShouldRefetchData,
  onItemSelect,
  isPopupMode,
  updateImageMetadata,
  popupWidth,
  suggestionODataFilter,
  customBackgroundColor,
  hideLevelNavigation,
  hideScrollHeader,
  disabledModes,
  setTotalSearchResult: setTotalSearchResultProp,
  searchInputWithModeSelectorStyle,
  jobMainAppTileView,
  searchInputViewSelectorBg,
  showModeSelectorInPopupMode,
  shouldUseDefaultHorizontalPaddings,
  defaultOrderBy,
  machines,
  users,
  tableNamePrefix,
}) => {
  const initialLevel = route?.params?.level || LEVELS.ALL;
  const initialSubLevel = route?.params?.subLevel || LEVELS.ALL;
  const initialSearch = route?.params?.search || '';
  // Get attachment mode from route params
  const attachMode = route?.params?.attachMode || false;
  const attachImageId = route?.params?.attachImageId || null;

  const { IS_NARROW_WEB } = useDeviceSize();

  const webDisplayMode = {
    [LEVELS.Parts]: searchDisplayMode.block,
    [LEVELS.ProductionOrders]: searchDisplayMode.tableStandard,
    [LEVELS.Operations]: searchDisplayMode.tableStandard,
    [LEVELS.ProductionBatches]: searchDisplayMode.infoTiles,
    [LEVELS.NCFiles]: searchDisplayMode.tableStandard,
    [LEVELS.Tools]: searchDisplayMode.tableStandard,
    [LEVELS.ImagesDocuments]: searchDisplayMode.block,
    [LEVELS.Machines]: searchDisplayMode.tableStandard,
    [LEVELS.Persons]: searchDisplayMode.tableStandard,
  };

  const tabletDisplayMode = {
    [LEVELS.Parts]: searchDisplayMode.block,
    [LEVELS.ProductionOrders]: searchDisplayMode.line,
    [LEVELS.Operations]: searchDisplayMode.line, // Keep original default but infoTiles available
    [LEVELS.ProductionBatches]: searchDisplayMode.infoTiles,
    [LEVELS.NCFiles]: searchDisplayMode.line,
    [LEVELS.Tools]: searchDisplayMode.line,
    [LEVELS.ImagesDocuments]: searchDisplayMode.block,
    [LEVELS.Machines]: searchDisplayMode.line,
    [LEVELS.Persons]: searchDisplayMode.line,
  };

  const phoneDisplayMode = {
    [LEVELS.Parts]: searchDisplayMode.line,
    [LEVELS.ProductionOrders]: searchDisplayMode.line,
    [LEVELS.Operations]: searchDisplayMode.line, // Keep original default but infoTiles available
    [LEVELS.ProductionBatches]: searchDisplayMode.infoTiles,
    [LEVELS.NCFiles]: searchDisplayMode.line,
    [LEVELS.Tools]: searchDisplayMode.line,
    [LEVELS.ImagesDocuments]: searchDisplayMode.list,
    [LEVELS.Machines]: searchDisplayMode.line,
    [LEVELS.Persons]: searchDisplayMode.line,
  };

  const defaultDisplayMode = isPhone && !IS_NARROW_WEB ? phoneDisplayMode : isWeb ? webDisplayMode : tabletDisplayMode;

  const persistedDisplayMode = !route?.params?.ignoreModeHistory ? getSecureValue(`${initialLevel}-displayMode`) || null : null;
  const initialDisplayMode = route?.params?.displayMode || persistedDisplayMode || defaultDisplayMode[initialLevel] || searchDisplayMode.block;

  let initialODataFilter = route?.params?.oDataFilter || null;

  let initialSortModel = null;
  let initialOrderBy = defaultOrderBy || null;
  let initialColumnVisibilityModel = {};
  let initialFilterModel = {};

  const prefix = tableNamePrefix ? `${tableNamePrefix}-` : '';
  const levelTableNameMap = {
    [LEVELS.Parts]: `${prefix}${TABLE_NAMES.part}`,
    [LEVELS.Operations]: `${prefix}${TABLE_NAMES.productionOperation}`,
    [LEVELS.ProductionBatches]: `${prefix}${TABLE_NAMES.productionBatch}`,
    [LEVELS.ProductionOrders]: `${prefix}${TABLE_NAMES.productionOrder}`,
    [LEVELS.NCFiles]: `${prefix}${TABLE_NAMES.ncFile}`,
    [LEVELS.Tools]: `${prefix}${TABLE_NAMES.tools}`,
    [LEVELS.ImagesDocuments]: `${prefix}${TABLE_NAMES.images}`,
  };

  try {
    if (route?.params?.sortModel) {
      initialSortModel = JSON.parse(route?.params?.sortModel);
    }
    if (route?.params?.orderBy) {
      initialOrderBy = JSON.parse(route?.params?.orderBy);
    }
    if (route?.params?.columnVisibilityModel) {
      initialColumnVisibilityModel = JSON.parse(route?.params?.columnVisibilityModel);
    }
    if (route?.params?.filterModel) {
      initialFilterModel = JSON.parse(route?.params?.filterModel);
    }

    // Fall back to localStorage persisted config if no route params
    const persisted = getPersistedTableConfig(levelTableNameMap[initialLevel]);
    if (persisted) {
      if (!initialSortModel && persisted.sortModel?.length) {
        initialSortModel = persisted.sortModel;
        if (!initialOrderBy) initialOrderBy = persisted.orderBy;
      }
      if (!initialFilterModel?.items?.length && persisted.filterModel?.items?.length) {
        initialFilterModel = persisted.filterModel;
        if (!initialODataFilter) initialODataFilter = persisted.oDataFilter;
      }
      if (!Object.keys(initialColumnVisibilityModel).length && persisted.columnVisibilityModel) {
        initialColumnVisibilityModel = persisted.columnVisibilityModel;
      }
    }
  } catch (e) {
    initialSortModel = null;
    initialOrderBy = null;
    initialODataFilter = null;
    initialColumnVisibilityModel = {};
    initialFilterModel = {};
  }

  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [sortModel, setSortModel] = useState(initialSortModel);
  const [oDataFilter, setODataFilter] = useState(initialODataFilter);
  const oDataFilterRef = useRef(initialODataFilter);
  useEffect(() => {
    oDataFilterRef.current = oDataFilter;
  }, [oDataFilter]);
  const [level, setLevel] = useState(initialLevel);
  const [filter, setFilter] = useState(initialSubLevel);
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [displayMode, setDisplayMode] = useState(initialDisplayMode);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initialColumnVisibilityModel);
  const [filterModel, setFilterModel] = useState(initialFilterModel);
  const [suggestion, setSuggestionLocal] = useState([]);
  const [searchResult, setSearchResultInternal] = useState([]);
  const [facets, setFacets] = useState(null);
  const [totalSearchResult, setTotalSearchResultInternal] = useState(null);
  const [history, setHistory] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [page, setPage] = useState(1);
  const [isMore, setIsMore] = useState(true);
  const shouldRerouteAttachLevel =
    attachMode && (initialLevel === LEVELS.Tools || initialLevel === LEVELS.ImagesDocuments || initialLevel === LEVELS.ProductionBatches);
  const shouldLoadInitialLevel = initialLevel !== LEVELS.ALL;
  const shouldSubmitInitialSearch = !!initialSearch;
  const willTriggerInitialSearch = shouldRerouteAttachLevel || shouldLoadInitialLevel || shouldSubmitInitialSearch;
  const [loaded, setLoaded] = useState(!willTriggerInitialSearch);
  const [isError, setIsError] = useState(false);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [alowLoadMore, setLoadMore] = useState(true);
  const [isRefReady, setIsRefReady] = useState(false);
  const [statisticTileName, setStatisticTileName] = useState('');

  const levelRef = useRef(initialLevel);
  const displayModeRef = useRef(initialDisplayMode);
  const batchListOffsetForTileMode = useRef(0);

  const isTableMode = displayMode === searchDisplayMode.tableStandard || displayMode === searchDisplayMode.tableCompact;

  useListenForClosedOperations({
    isActiveInstance: level === LEVELS.Operations,
    setJobs: setSearchResultInternal,
  });

  useListenForUploadedOperationImages({
    isActiveInstance: level === LEVELS.Operations,
    setJobs: setSearchResultInternal,
  });

  const topAmount = isWeb ? 100 : 15;
  const topAmountForBatchesTileMode = 10;

  const setTotalSearchResult = (result) => {
    setTotalSearchResultProp?.(result);
    setTotalSearchResultInternal(result);
  };

  const setSearchResult = (data) => {
    if (levelRef.current === LEVELS.ProductionBatches) {
      setBatchList(data);
    }
    setSearchResultInternal(data);
  };

  const fadeAnim = useRef(new Animated.Value(1)).current;
  const scrollAnim = useRef(new Animated.Value(0)).current;
  const searchRef = useRef(null);
  const abortControllerSuggestion = useRef();
  const customAnchorRef = useRef(null);
  const apiRef = (isWeb ? () => useGridApiRef() : () => null)();

  const isTileMode = displayMode === searchDisplayMode.infoTiles;

  useEffect(() => {
    if (customAnchorRef.current) {
      setIsRefReady(true);
    }
  }, [customAnchorRef]);

  // Track search opened with dedupe (no useFocusEffect, not a navigation screen)
  useEffect(() => {
    trackOpenedEvent(TRACKING_EVENTS.SEARCH, {});
  }, []);

  // Handle attachment mode - switch to Parts if Tools, Images & Documents, or Batches is selected
  useEffect(() => {
    if (shouldRerouteAttachLevel) {
      changeLevel(LEVELS.Parts, true);
      return;
    }

    if (shouldLoadInitialLevel) {
      changeLevel(level, true);
    }

    if (shouldSubmitInitialSearch) {
      submit(searchValue);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const history = getSecureValue('searchHistory');
      if (history) {
        setHistory(JSON.parse(history));
      }
      const recent = getSecureValue('recentlyViewed');
      if (recent) {
        setRecentlyViewed(JSON.parse(recent));
      }
    })();
  }, []);

  useEffect(() => {
    if (shouldRefetchData) {
      submit();
      setShouldRefetchData(false);
    }

    return () => setShouldRefetchData(false);
  }, [shouldRefetchData]);

  useEffect(() => {
    setGlobalSelectedLevel?.(level);
  }, [level, setGlobalSelectedLevel]);

  const setSelectedLevel = async (data) => {
    levelRef.current = data;
    batchListOffsetForTileMode.current = 0;

    setLevel(data);

    if (!route?.params?.ignoreModeHistory) {
      const mode = getSecureValue(`${data}-displayMode`);
      if (mode) {
        setSelectedDisplayMode(mode, data);
        displayModeRef.current = mode;
      } else {
        if (defaultDisplayMode[data]) {
          setSelectedDisplayMode(defaultDisplayMode[data], data);
          displayModeRef.current = defaultDisplayMode[data];
        }
      }
    }
    if (data === LEVELS.ALL) {
      return removeQueryParam('level');
    }
    addQueryParams({ level: data });
  };

  const setSelectedFilter = (data) => {
    setFilter(data);
    if (!data || data === LEVELS.ALL) {
      return removeQueryParam('subLevel');
    }
    addQueryParams({ subLevel: data });
  };

  const setSelectedSearch = (data) => {
    setSearchValue(data);
    if (!data) {
      return removeQueryParam('search');
    }
    addQueryParams({ search: data });
  };

  const setSelectedDisplayMode = async (data, localLevel = level) => {
    batchListOffsetForTileMode.current = 0;
    setDisplayMode(data);

    setSecureValue(`${localLevel}-displayMode`, data);
    if (data === searchDisplayMode.block) {
      return removeQueryParam('displayMode');
    }
    addQueryParams({ displayMode: data });
  };

  const resetColumnVisibilityModel = () => {
    setColumnVisibilityModel(null);
    removeQueryParam('columnVisibilityModel');
  };

  const resetFilterModel = () => {
    setFilterModel(null);
    oDataFilterRef.current = null;
    setODataFilter(null);
    removeQueryParam('filterModel');
    removeQueryParam('oDataFilter');
    clearPersistedFilters(levelTableNameMap[level]);
  };

  const resetSortModel = () => {
    setSortModel(null);
    setOrderBy(null);
    removeQueryParam('sortModel');
    removeQueryParam('orderBy');
    clearPersistedSort(levelTableNameMap[level]);
  };

  const resetTableSettings = () => {
    resetColumnVisibilityModel();
    resetFilterModel();
    resetSortModel();
  };

  const setSuggestion = (s) => {
    if (abortControllerSuggestion.current) {
      abortControllerSuggestion.current.abort();
    }

    setSuggestionLocal(s);
  };

  const clearHistory = () => {
    setHistory([]);
    setSecureValue('searchHistory', '');
  };

  const clearRecentlyViewed = () => {
    setRecentlyViewed([]);
    setSecureValue('recentlyViewed', '');
  };

  const cancelAnimation = async () => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSelectedSearch('');
    setSuggestion([]);
    setSearchResult([]);
    setTotalSearchResult(null);
    setIsSearchTriggered(false);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 300,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
    if (LEVELS.ALL !== level) {
      await search({ level, searchValue: '', filter: null });
    }
  };

  const suggestionAPI = {
    [LEVELS.ALL]: globalSuggestion,
    [LEVELS.Parts]: partsSuggestion,
    [LEVELS.Operations]: operationsSuggestion,
    [LEVELS.ProductionBatches]: productionBatchesSuggestion,
    [LEVELS.ProductionOrders]: productionOrdersSuggestion,
    [LEVELS.NCFiles]: ncFilesSuggestion,
    [LEVELS.Tools]: toolsSuggestion,
    [LEVELS.ImagesDocuments]: documentsSuggestion,
  };

  const onSearch = (v) => {
    setSearchValue(v);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!v) {
      setSuggestion([]);
      return;
    }

    searchTimeout = setTimeout(async () => {
      const d = {
        ...(suggestionODataFilter ? { oDataFilter: suggestionODataFilter } : {}),
        searchText: v,
        useFuzzyMatching: false,
        size: 10,
        highlight: {
          preTag: '<p>',
          postTag: '<p>',
        },
      };

      try {
        const suggestion = await controlAPICall(suggestionAPI[level], d, abortControllerSuggestion);
        setSuggestion(suggestion || []);
      } catch (error) {
        console.error('Error in suggestion API call:', error);
        setSuggestion([]);
      }
    }, 300);
  };

  const submitAPI = {
    [LEVELS.ALL]: globalSearch,
    [LEVELS.Parts]: partsSearch,
    [LEVELS.Operations]: operationsSearch,
    [LEVELS.ProductionBatches]: productionBatchesSearch,
    [LEVELS.ProductionOrders]: productionOrdersSearch,
    [LEVELS.NCFiles]: ncFilesSearch,
    [LEVELS.Tools]: toolsSearch,
    [LEVELS.ImagesDocuments]: documentsSearch,
    [LEVELS.Machines]: ({ data: { search } }) => {
      if (machines.length) {
        return filterByKeys({ items: machines, search, keys: ['fullName'] });
      }
      return getMachines();
    },
    [LEVELS.Persons]: ({ data: { search } }) => {
      if (users.length) {
        return filterByKeys({ items: users, search, keys: ['userProfile/firstName', 'userProfile/lastName'] });
      }
      return searchUsers({ reset: true });
    },
  };

  const selectProps = {
    [LEVELS.ALL]: false,
    [LEVELS.Parts]: PART_SELECT,
    [LEVELS.Operations]: OPERATION_SELECT,
    [LEVELS.ProductionBatches]: BATCH_LIST_SELECT,
    [LEVELS.ProductionOrders]: PRODUCTION_ORDERS_SELECT,
    [LEVELS.NCFiles]: NC_FILE_SELECT,
    [LEVELS.Tools]: false,
    [LEVELS.ImagesDocuments]: IMAGES_DOCUMENTS_SELECT,
    [LEVELS.Machines]: false,
    [LEVELS.Persons]: false,
  };

  const getDefaultOrderBy = (level) => {
    if (defaultOrderBy) return defaultOrderBy;
    return level === LEVELS.NCFiles ? ['CreatedOn desc'] : ['ModifiedOn desc'];
  };

  const search = async ({
    level: levelInternal = level,
    searchValue: searchValueInternal = searchValue,
    filter: filterInternal = filter,
    oDataFilter: oDataFilterInternal = oDataFilterRef.current,
    orderBy: orderByInternal = orderBy,
    filterModel: filterModelInternal = filterModel,
    resetHistory,
  } = {}) => {
    if (levelInternal !== levelRef.current) {
      return;
    }

    setLoaded(false);
    setIsError(false);

    const formattedOrderBy = orderByInternal?.length ? orderByInternal : getDefaultOrderBy(levelInternal);

    const searchFields = filterModelInternal?.items?.map((i) => capitalizeFirstLetter(i.field));

    const filterStorage = {
      [LEVELS.ALL]: null,
      [LEVELS.Parts]: {
        oDataFilter: oDataFilterInternal,
        orderBy: formattedOrderBy,
        searchFields,
      },
      [LEVELS.Operations]: {
        oDataFilter: oDataFilterInternal,
        orderBy: formattedOrderBy,
        searchFields,
      },
      [LEVELS.ProductionBatches]: {
        oDataFilter: oDataFilterInternal,
        orderBy: formattedOrderBy,
        searchFields,
      },
      [LEVELS.ProductionOrders]: {
        oDataFilter: oDataFilterInternal,
        orderBy: formattedOrderBy,
        searchFields,
      },
      [LEVELS.NCFiles]: { oDataFilter: oDataFilterInternal, orderBy: formattedOrderBy, searchFields },
      [LEVELS.Tools]: {
        oDataFilter: oDataFilterInternal,
        orderBy: formattedOrderBy,
        searchFields,
        select: ['Description', 'Memo', 'NcFileCount', 'StickoutUM', 'ToolId', 'ToolName'],
      },
      [LEVELS.ImagesDocuments]: { oDataFilter: oDataFilterInternal, orderBy: formattedOrderBy, searchFields },
      [LEVELS.Machines]: { oDataFilter: oDataFilterInternal, orderBy: formattedOrderBy, searchFields },
      [LEVELS.Persons]: { oDataFilter: oDataFilterInternal, orderBy: formattedOrderBy, searchFields },
    };

    const res = await submitAPI[levelInternal]({
      data: {
        search: searchValueInternal,
        top: topAmount,
        skip: 0,
      },
      filter: filterStorage[levelInternal],
      select: selectProps[levelInternal],
      resetHistory,
    });

    const result = Array.isArray(res) ? res : res?.searchResult ?? res?.users;
    const resultCount = Array.isArray(res) ? res.length : res?.totalResultCount ?? res?.totalCount;

    if (result) {
      setTotalSearchResult(resultCount);
      setSearchResult(result);
      if (res.facets) {
        setFacets(res.facets);
      }
    } else {
      setIsError(true);
    }
    setLoaded(true);
    setIsSearchTriggered(searchValueInternal);
  };

  const defaultFilters = {
    [LEVELS.Parts]: PART_LEVELS.All,
    [LEVELS.Operations]: PART_LEVELS.All,
    [LEVELS.ProductionBatches]: PART_LEVELS.All,
    [LEVELS.ProductionOrders]: PART_LEVELS.All,
    [LEVELS.NCFiles]: NC_FILE_LEVELS.All,
    [LEVELS.Tools]: TOOL_LEVELS.All,
    [LEVELS.ImagesDocuments]: IMAGE_DOCUMENTS_LEVELS.All,
    [LEVELS.Machines]: IMAGE_DOCUMENTS_LEVELS.All,
    [LEVELS.Persons]: IMAGE_DOCUMENTS_LEVELS.All,
  };

  const changeLevel = async (level, isInitialLoad) => {
    const persisted = !isInitialLoad ? getPersistedTableConfig(levelTableNameMap[level]) : null;

    if (!isInitialLoad) {
      oDataFilterRef.current = persisted?.oDataFilter || null;
      setSelectedFilter(defaultFilters[level]);
      setSortModel(persisted?.sortModel || null);
      setOrderBy(persisted?.orderBy || null);
      setODataFilter(persisted?.oDataFilter || null);
      setFilterModel(persisted?.filterModel || null);
      setColumnVisibilityModel(persisted?.columnVisibilityModel || null);

      trackEvent(TRACKING_EVENTS.SEARCH, {
        action: 'levelChanged',
        itemType: levelToSearchItemType(level),
      });
    }

    setStatisticTileName('');
    setSearchResult([]);
    setFacets(null);
    setSuggestion([]);
    setTotalSearchResult(null);
    setIsMore(true);
    setLoadMore(true);
    setPage(1);

    await setSelectedLevel(level);

    const listWhereNotAllModeSupported = [LEVELS.ALL];

    if (listWhereNotAllModeSupported.includes(level)) {
      await setSelectedDisplayMode(searchDisplayMode.block, level);
    }

    if (!isInitialLoad) {
      removeQueryParam('sortModel');
      removeQueryParam('orderBy');
      removeQueryParam('filterModel');
      removeQueryParam('oDataFilter');
      removeQueryParam('columnVisibilityModel');

      if (level === LEVELS.ALL && !searchValue) {
        return;
      }

      await search({
        level,
        searchValue,
        filter,
        oDataFilter: persisted?.oDataFilter || null,
        orderBy: persisted?.orderBy || null,
        filterModel: persisted?.filterModel || null,
        resetHistory: true,
      });
      return;
    }

    if (level === LEVELS.ALL && !searchValue) {
      return;
    }

    await search({ level, searchValue, filter });
  };

  const changeFilter = async (filter) => {
    setSelectedFilter(filter);
    setStatisticTileName('');
    setSearchResult([]);
    setFacets(null);
    setSuggestion([]);
    setTotalSearchResult(null);
    setIsMore(true);
    setLoadMore(true);
    setPage(1);

    await search({ level, searchValue, filter });
  };

  const submit = async (v) => {
    setIsMore(true);
    setPage(1);
    setSuggestion([]);

    const text = v && typeof v === 'string' ? v : searchValue;

    setSelectedSearch(text);

    if (!text && level === LEVELS.ALL) {
      setSearchResult([]);
      setFacets(null);
      return;
    }
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    if (!history.includes(text)) {
      const h = [text, ...history];
      setSecureValue('searchHistory', JSON.stringify(h));
      const noSpaceLine = String(text).replace(/ /g, '');
      if (noSpaceLine) {
        setHistory(h);
      }
    }

    resetFilterModel();

    await search({ level, searchValue: text, filter, oDataFilter: null, filterModel: null });
    trackEvent(TRACKING_EVENTS.SEARCH, {
      action: 'executed',
      searchLength: text?.length || 0,
      resultCount: totalSearchResult || 0,
      searchLevel: levelToSearchItemType(level),
    });
  };

  const loadMore = async (callback) => {
    if (
      levelRef.current === LEVELS.ProductionBatches &&
      isTileMode &&
      searchResult.length &&
      batchListOffsetForTileMode.current + topAmountForBatchesTileMode < searchResult.length
    ) {
      callback();
      batchListOffsetForTileMode.current += topAmountForBatchesTileMode;
      return;
    }

    if (!isMore) {
      return;
    }

    const isCallback = callback && typeof callback === 'function';

    const res = await submitAPI[level]({
      data: {
        search: searchValue,
        top: topAmount,
        skip: page * topAmount,
      },
    });

    if (!res?.searchResult?.length) {
      setIsMore(false);
      if (isCallback) {
        callback();
      }
      return;
    }
    setSearchResult([...searchResult, ...res.searchResult]);
    batchListOffsetForTileMode.current += topAmount;
    setPage(page + 1);
    if (isCallback) {
      callback();
    }
  };

  const showHistory = Boolean((recentlyViewed?.length || history?.length) && !searchResult.length && loaded);

  const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }) => {
    return layoutMeasurement.height + contentOffset.y >= contentSize.height - 1500;
  };

  let scrollTimeoutId = null;

  const scrollListener = async (event, forceLoad) => {
    const nativeEvent = event.nativeEvent;

    if (levelRef.current === LEVELS.ProductionBatches) {
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }

      scrollTimeoutId = setTimeout(() => {
        const y = nativeEvent?.contentOffset?.y;
        setBatchListYPosition(y);
        scrollTimeoutId = null;
      }, 100);
    }

    if (forceLoad || (isCloseToBottom(nativeEvent) && alowLoadMore)) {
      setLoadMore(false);
      const callback = () => {
        setTimeout(() => {
          setLoadMore(true);
        }, 1000);
      };
      await loadMore(callback);
    }
  };

  const renderProps = {
    scale,
    font,
    searchResult,
    facets,
    C,
    generateImageURL,
    loadMore,
    setRecentlyViewed,
    totalSearchResult,
    fetchData: submit,
    loaded,
    isError,
    filter,
    searchValue,
    history: history.slice(0, 5),
    recentlyViewed: recentlyViewed.slice(0, 5),
    searchRef,
    setSearchValue,
    onSearch,
    clearHistory,
    clearRecentlyViewed,
    level,
    submit,
    displayMode,
    isSmallMode: displayMode === searchDisplayMode.square,
    isLineMode: displayMode === searchDisplayMode.line,
    isInfoTilesMode: displayMode === searchDisplayMode.infoTiles,
    isBlockMode: displayMode === searchDisplayMode.block,
    isTableMode,
    setSelectedNCFile,
    isSearchTriggered,
    changeLevel,
    changeFilter,
    featureFlags,
    apiRef,
    customAnchorRef,
    columnVisibilityModel,
    sortModel,
    search,
    density: displayMode || 'standard',
    setColumnVisibilityModel,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    setSortModel,
    oDataFilter,
    setODataFilter,
    onRowsScrollEnd: () => scrollListener({}, true),
    clearSearchAndFilters: () => {
      setSelectedSearch('');
      resetFilterModel();
      search({ searchValue: '', oDataFilter: null, filterModel: null });
    },
    batchListOffsetForTileMode: batchListOffsetForTileMode.current,
    topAmount: topAmountForBatchesTileMode,
    IS_NARROW_WEB,
    batchList,
    customBackgroundColor,
    attachMode,
    attachImageId,
    updateImageMetadata,
    onItemSelect,
    isPopupMode,
    popupWidth,
    jobMainAppTileView,
    shouldUseDefaultHorizontalPaddings,
    tableName: levelTableNameMap[level],
  };

  const headerName = {
    [LEVELS.ALL]: i18n.t('Search'),
    [LEVELS.Parts]: i18n.t('Parts'),
    [LEVELS.Operations]: i18n.t('Jobs'),
    [LEVELS.ProductionBatches]: i18n.t('Batches'),
    [LEVELS.ProductionOrders]: i18n.t('Production Orders'),
    [LEVELS.NCFiles]: i18n.t('NC Files'),
    [LEVELS.Tools]: i18n.t('Tools'),
    [LEVELS.ImagesDocuments]: i18n.t('Images & Documents'),
    [LEVELS.Machines]: i18n.t('Machines'),
    [LEVELS.Persons]: i18n.t('Persons'),
  };
  const inputName = {
    [LEVELS.ALL]: '',
    [LEVELS.Parts]: 'Parts',
    [LEVELS.Operations]: 'Jobs',
    [LEVELS.ProductionBatches]: 'Batches',
    [LEVELS.ProductionOrders]: 'Production Orders',
    [LEVELS.NCFiles]: 'NC Files',
    [LEVELS.Tools]: 'Tools',
    [LEVELS.ImagesDocuments]: 'Images & Documents',
    [LEVELS.Machines]: 'Machines',
    [LEVELS.Persons]: 'Persons',
  };

  const scrollProps = {
    style: [
      { width: '100%', backgroundColor: customBackgroundColor || C('Background / Light / System'), paddingTop: isPopupMode ? 0 : scale(10), flex: 1 },
    ],
    contentContainerStyle: [
      {
        width: isPopupMode && popupWidth ? popupWidth : '100%',
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'center',
        paddingBottom: scale(isPopupMode ? 0 : 100),
      },
      (isError || !loaded) && {
        height: '100%',
      },
      isTableMode && {
        flex: 1,
        paddingBottom: 0,
      },
    ],
    scrollEventThrottle: 1,
    onScroll: Animated.event([{ nativeEvent: { contentOffset: { y: scrollAnim } } }], {
      useNativeDriver: false,
      listener: scrollListener,
    }),
  };

  const startHeaderY = isPhone && !IS_NARROW_WEB ? 52 : 62;
  const startLevelsY = isPhone && !IS_NARROW_WEB ? 84 : 94;

  if (!isPopupMode && ((isPhone && !IS_NARROW_WEB) || !isWeb)) {
    return (
      <MobileHeader
        {...{
          displayMode,
          setSelectedDisplayMode,
          title: headerName[level],
          suggestion,
          C,
          onSearch,
          setSuggestion,
          searchRef,
          cancelAnimation,
          setSelectedNCFile,
          renderProps,
          searchValue,
          submit,
          loaded,
          isError,
          totalSearchResult,
          isAllLevel: level === LEVELS.ALL,
          alowLoadMore,
          setLoadMore,
          loadMore,
          level,
          setBatchListContainerHeight,
          setBatchListYPosition,
          scrollListener: scrollListener,
          isPopupMode,
          width: isPopupMode && popupWidth ? popupWidth : width,
          popupWidth,
          scale,
          font,
          searchInputViewSelectorBg,
        }}
      >
        <View
          style={{
            width: '100%',
          }}
        >
          {!hideLevelNavigation && (
            <View style={{ marginTop: scale(16) }}>
              <Level
                {...{
                  C,
                  level,
                  changeLevel,
                  changeFilter,
                  filter,
                  featureFlags,
                  width: isPopupMode && popupWidth ? popupWidth : width,
                  attachMode,
                  isPopupMode,
                  scale,
                  font,
                }}
              />
            </View>
          )}
          {LEVELS.ALL === level ? (
            showHistory && !isSearchTriggered ? (
              <History {...renderProps} />
            ) : (
              <SearchResults {...renderProps} />
            )
          ) : (
            <View style={{ flex: 1, width: isPopupMode && popupWidth ? popupWidth : '100%', alignSelf: 'center' }}>
              <Statistic
                {...{
                  C,
                  level,
                  oDataFilter,
                  searchValue,
                  isSearchTriggered,
                  statisticTileName,
                  setStatisticTileName,
                  search,
                  facets,
                  loaded,
                  isPopupMode,
                  popupWidth,
                  scale,
                  font,
                }}
              />
              <View style={{ marginTop: scale(16) }}>
                <RenderSearchComponent {...renderProps} />
              </View>
            </View>
          )}
        </View>
      </MobileHeader>
    );
  }

  return (
    <Animated.View style={{ flex: 1, width: '100%', backgroundColor: customBackgroundColor || C('Background/Light/System'), flexDirection: 'row' }}>
      {!hideScrollHeader && (
        <Animated.View
          style={{
            position: 'absolute',
            zIndex: 10000000,
            width: '100%',
            height: scrollAnim.interpolate({
              inputRange: [0, scale(startHeaderY), scale(startHeaderY)],
              outputRange: [0, 0, scale(150)],
            }),
          }}
        >
          <Animated.View
            style={{
              opacity: scrollAnim.interpolate({
                inputRange: [scale(startHeaderY), scale(startHeaderY + 1)],
                outputRange: [0, 1],
              }),
              top: scrollAnim.interpolate({
                inputRange: [0, scale(startHeaderY), scale(startHeaderY)],
                outputRange: [-100, -100, 0],
              }),
            }}
          >
            <AnimatedHeader
              {...{
                C,
                text: headerName[level],
                animated: false,
                position: 'relative',
                back: LEVELS.ALL !== level && isPhone && !IS_NARROW_WEB,
                onBackPress: () => changeLevel(LEVELS.ALL),
              }}
            />
          </Animated.View>
          <Animated.View
            style={{
              paddingTop: scale(18),
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: customBackgroundColor || C('Background / Light / System'),
              opacity: scrollAnim.interpolate({
                inputRange: [scale(startLevelsY), scale(startLevelsY + 1)],
                outputRange: [0, 1],
              }),
              top: scrollAnim.interpolate({
                inputRange: [0, scale(startLevelsY), scale(startLevelsY)],
                outputRange: [-100, -100, 0],
              }),
            }}
          >
            <Level
              {...{
                C,
                level,
                changeLevel,
                changeFilter,
                filter,
                featureFlags,
                width: isPopupMode && popupWidth ? popupWidth : width,
                attachMode,
                scale,
              }}
            />
          </Animated.View>
        </Animated.View>
      )}
      <Animated.View
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setBatchListContainerHeight(height);
        }}
      >
        <Animated.ScrollView key="webList" testID={TEST_IDS.searchScreen.scrollView} {...scrollProps}>
          <View style={{ alignItems: 'center', zIndex: 10 }}>
            <View style={{ width: isPopupMode && popupWidth ? popupWidth : fullWidth }}>
              {/*<View style={{ width: fullWidth }}>*/}
              <View style={{ display: 'flex', flexDirection: 'row', zIndex: 10 }}>
                <Header
                  {...{
                    C,
                    fadeAnim,
                    cancelAnimation,
                    // onFocus,
                    // onBlur,
                    searchRef,
                    searchValue,
                    setSearchValue,
                    onSearch,
                    submit,
                    suggestion,
                    name: headerName[level],
                    inputName: inputName[level],
                    changeLevel,
                    level,
                    setSuggestion,
                    // isFocused,
                    setSelectedNCFile,
                    // showCancel,
                    totalSearchResult,
                    loaded,
                    isError,
                    displayMode,
                    setSelectedDisplayMode,
                    apiRef,
                    isRefReady,
                    isTableMode,
                    customAnchorRef,
                    isWithERP,
                    isPopupMode,
                    attachMode,
                    attachImageId,
                    updateImageMetadata,
                    onItemSelect,
                    popupWidth,
                    scale,
                    font,
                    disabledModes,
                    searchInputWithModeSelectorStyle,
                    searchInputViewSelectorBg,
                    showModeSelectorInPopupMode,
                  }}
                />
              </View>
              {!hideLevelNavigation && (
                <Level
                  {...{
                    C,
                    level,
                    changeLevel,
                    changeFilter,
                    filter,
                    featureFlags,
                    width: isPopupMode && popupWidth ? popupWidth : width,
                    attachMode,
                    isPopupMode,
                    scale,
                    font,
                  }}
                />
              )}
            </View>
          </View>
          {LEVELS.ALL === level ? (
            showHistory && !isSearchTriggered ? (
              <History {...renderProps} />
            ) : (
              <SearchResults {...renderProps} />
            )
          ) : (
            <View style={{ flex: 1, width: isPopupMode && popupWidth ? popupWidth : '100%', alignSelf: 'center' }}>
              <Statistic
                {...{
                  C,
                  level,
                  oDataFilter,
                  searchValue,
                  isSearchTriggered,
                  statisticTileName,
                  setStatisticTileName,
                  search,
                  facets,
                  loaded,
                  isPopupMode,
                  popupWidth,
                  scale,
                  font,
                }}
              />
              <Animated.ScrollView key="mobileList" {...scrollProps} scrollEnabled={!isWeb}>
                <RenderSearchComponent {...renderProps} />
              </Animated.ScrollView>
            </View>
          )}
        </Animated.ScrollView>
      </Animated.View>
      <Suggestion
        {...{
          suggestion,
          C,
          onSearch,
          setSuggestion,
          searchRef,
          cancelAnimation,
          setSelectedNCFile,
          attachMode,
          attachImageId,
          updateImageMetadata,
          onItemSelect,
          isPopupMode,
          popupWidth,
          scale,
          font,
        }}
      />
    </Animated.View>
  );
};

export default connect(
  ({ user: { featureFlags }, membership: { isWithERP, users }, batch: { batchList }, search: { shouldRefetchData }, machines: { machines } }) => ({
    featureFlags,
    isWithERP,
    batchList,
    shouldRefetchData,
    machines,
    users,
  }),
  {
    globalSuggestion,
    partsSuggestion,
    operationsSuggestion,
    productionBatchesSuggestion,
    productionOrdersSuggestion,
    toolsSuggestion,
    ncFilesSuggestion,
    documentsSuggestion,
    globalSearch,
    generateImageURL,
    partsSearch,
    operationsSearch,
    productionBatchesSearch,
    productionOrdersSearch,
    ncFilesSearch,
    toolsSearch,
    documentsSearch,
    setSelectedNCFile,
    setGlobalSelectedLevel,
    setBatchList,
    setBatchListYPosition,
    setBatchListContainerHeight,
    setShouldRefetchData,
    updateImageMetadata,
    getMachines,
    searchUsers,
  }
)(ColorWrapper(Search));
